// src/components/modules/trading/StreamingRecommendations.tsx
import React, { useState, useRef, useEffect } from 'react';
import { TradeRecommendation } from '../../../providers/tradingTypes';
import { getTradingRecommendationStream, getMarketAnalysisStream } from '../../../service/llmStreamingService';
import RecommendationItem from './RecommendationItem';

interface StreamingRecommendationsProps {
  symbol: string;
  timeframe: string;
  marketData: any[];
  onNewRecommendation?: (recommendation: TradeRecommendation) => void;
  language?: 'es' | 'en';
  riskLevel?: 'low' | 'medium' | 'high';
}

const StreamingRecommendations: React.FC<StreamingRecommendationsProps> = ({
  symbol,
  timeframe,
  marketData,
  onNewRecommendation,
  language = 'es',
  riskLevel = 'medium'
}) => {
  // Estados para streaming y recomendaciones
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamingText, setStreamingText] = useState<string>('');
  const [mode, setMode] = useState<'recommendation' | 'analysis'>('recommendation');
  const [activeRequest, setActiveRequest] = useState<AbortController | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);

  // Textos según idioma
  const texts = {
    title: language === 'es' ? 'Consulta a IA' : 'AI Query',
    startRecommendation: language === 'es' ? 'Solicitar Recomendación' : 'Request Recommendation',
    startAnalysis: language === 'es' ? 'Solicitar Análisis' : 'Request Analysis',
    cancel: language === 'es' ? 'Cancelar' : 'Cancel',
    thinking: language === 'es' ? 'Analizando el mercado...' : 'Analyzing the market...',
    switchToRecommendation: language === 'es' ? 'Recomendación' : 'Recommendation',
    switchToAnalysis: language === 'es' ? 'Análisis' : 'Analysis',
    analysisPrompt: language === 'es' 
      ? `Analiza técnicamente ${symbol} en timeframe ${timeframe}`
      : `Technically analyze ${symbol} in ${timeframe} timeframe`,
    recommendationPrompt: language === 'es'
      ? `Dame una recomendación de trading para ${symbol} en timeframe ${timeframe}`
      : `Give me a trading recommendation for ${symbol} in ${timeframe} timeframe`,
    waitingResponse: language === 'es' ? 'Esperando respuesta...' : 'Waiting for response...',
    errorFetching: language === 'es' 
      ? 'Error al obtener respuesta. Intenta nuevamente.' 
      : 'Error fetching response. Please try again.',
  };

  // Efecto para hacer scroll automático mientras se recibe respuesta
  useEffect(() => {
    if (isStreaming && streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [streamingText, isStreaming]);

  // Iniciar la solicitud de recomendación
  const startRecommendationRequest = () => {
    setIsStreaming(true);
    setStreamingText('');
    
    // Crear controlador para poder cancelar la solicitud
    const controller = new AbortController();
    setActiveRequest(controller);
    
    // Solicitar recomendación con streaming
    getTradingRecommendationStream(
      {
        symbol,
        timeframe,
        marketData,
        riskLevel
      },
      // Callback para actualizaciones parciales
      (update) => {
        setStreamingText(prev => prev + update);
      },
      // Callback para respuesta completa
      (recommendation) => {
        setIsStreaming(false);
        setActiveRequest(null);
        
        // Notificar al componente padre sobre la nueva recomendación
        if (onNewRecommendation) {
          onNewRecommendation(recommendation);
        }
      }
    );
  };

  // Iniciar la solicitud de análisis
  const startAnalysisRequest = () => {
    setIsStreaming(true);
    setStreamingText('');
    
    // Crear controlador para poder cancelar la solicitud
    const controller = new AbortController();
    setActiveRequest(controller);
    
    // Solicitar análisis con streaming
    getMarketAnalysisStream(
      {
        symbol,
        timeframe,
        marketData
      },
      // Callback para actualizaciones parciales
      (update) => {
        setStreamingText(prev => prev + update);
      },
      // Callback para respuesta completa
      (analysis) => {
        setIsStreaming(false);
        setActiveRequest(null);
      }
    );
  };

  // Cancelar la solicitud actual
  const cancelRequest = () => {
    if (activeRequest) {
      activeRequest.abort();
      setActiveRequest(null);
    }
    setIsStreaming(false);
  };

  // Iniciar solicitud según el modo seleccionado
  const startRequest = () => {
    if (mode === 'recommendation') {
      startRecommendationRequest();
    } else {
      startAnalysisRequest();
    }
  };

  return (
    <div className="streaming-recommendations card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{texts.title}</h5>
        
        {/* Selector de modo (recomendación/análisis) */}
        <div className="btn-group btn-group-sm" role="group">
          <button 
            type="button" 
            className={`btn ${mode === 'recommendation' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('recommendation')}
            disabled={isStreaming}
          >
            {texts.switchToRecommendation}
          </button>
          <button 
            type="button" 
            className={`btn ${mode === 'analysis' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('analysis')}
            disabled={isStreaming}
          >
            {texts.switchToAnalysis}
          </button>
        </div>
      </div>
      
      <div className="card-body">
        {/* Botones de acción */}
        <div className="mb-3 d-flex justify-content-center">
          {!isStreaming ? (
            <button 
              className="btn btn-primary" 
              onClick={startRequest}
            >
              {mode === 'recommendation' ? texts.startRecommendation : texts.startAnalysis}
            </button>
          ) : (
            <button 
              className="btn btn-danger" 
              onClick={cancelRequest}
            >
              {texts.cancel}
            </button>
          )}
        </div>
        
        {/* Área de visualización de streaming */}
        {(isStreaming || streamingText) && (
          <div className="streaming-container">
            {isStreaming && (
              <div className="streaming-status d-flex align-items-center mb-2">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">{texts.thinking}</span>
                </div>
                <span>{texts.thinking}</span>
              </div>
            )}
            
            <div 
              className="streaming-content border rounded p-3" 
              style={{ 
                maxHeight: '400px', 
                overflowY: 'auto',
                whiteSpace: 'pre-wrap'
              }}
              ref={streamRef}
            >
              {streamingText || texts.waitingResponse}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamingRecommendations;
